'use client';

import React, { useState, useRef, useEffect } from 'react';
import {
  Zap, CheckCircle, XCircle, CheckSquare, Send, MessageSquare, ChevronDown, ChevronUp,
} from 'lucide-react';
import { toast } from 'sonner';
import Avatar from '@/components/ui/Avatar';
import BranchTag from '@/components/ui/BranchTag';
import { useNeedsAttention } from '@/hooks/useNeedsAttention';
import { useRoomMessages } from '@/hooks/useRoomMessages';


type Branch = 'live_events' | 'studio' | 'outreach' | 'internal';

// ─── Static pending approvals (same source as PendingApprovalsWidget) ──────────
const PENDING_APPROVALS = [
  { id: 'tor-001', staff: 'Molly Nicholson',    title: 'Co-Director of Education',    dates: 'Apr 14–15', type: 'Personal', affectsItems: 3 },
  { id: 'tor-002', staff: 'Christopher Fahmie', title: 'Asst. Arts Administrator',    dates: 'Apr 21',    type: 'Medical',  affectsItems: 1 },
];

// ─── Quick reply room ──────────────────────────────────────────────────────────
const QUICK_REPLY_ROOM = { id: 'room-spring-showcase', name: 'Spring Showcase' };

export default function QuickActionsPanel() {
  const [collapsed, setCollapsed] = useState(false);

  // Approvals state
  const [approvals, setApprovals] = useState(PENDING_APPROVALS);

  // Gaps
  const { items: gapItems, resolveItem } = useNeedsAttention();
  const urgentGaps = gapItems.filter((g) => g.is_urgent).slice(0, 3);

  // Reply
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const { sendMessage } = useRoomMessages(QUICK_REPLY_ROOM.id);
  const inputRef = useRef<HTMLInputElement>(null);

  function handleApprove(id: string, name: string) {
    setApprovals((prev) => prev.filter((a) => a.id !== id));
    toast.success(`Time off approved for ${name}`);
    // Backend: PATCH /api/time-off-requests/:id { status: 'approved' }
  }

  function handleDeny(id: string, name: string) {
    setApprovals((prev) => prev.filter((a) => a.id !== id));
    toast.error(`Time off denied for ${name}`);
    // Backend: PATCH /api/time-off-requests/:id { status: 'denied' }
  }

  async function handleResolveGap(id: string, title: string) {
    await resolveItem(id);
    toast.success(`Gap resolved: ${title}`);
  }

  async function handleSendReply() {
    const text = replyText.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      await sendMessage(text);
      setReplyText('');
      toast.success(`Message sent to #${QUICK_REPLY_ROOM.name}`);
    } catch {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  }

  const totalActions = approvals.length + urgentGaps.length;

  return (
    <div className="bg-white rounded-xl border border-stone-100 shadow-card overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="w-full px-4 py-3 border-b border-stone-100 flex items-center gap-2 hover:bg-stone-50/60 transition-colors text-left"
      >
        <Zap size={14} className="text-amber-500 shrink-0" />
        <h3 className="text-[13px] font-semibold text-slate-700 flex-1">Quick Actions</h3>
        {totalActions > 0 && (
          <span className="text-[11px] font-semibold bg-amber-50 text-amber-600 px-1.5 py-0.5 rounded-full">
            {totalActions}
          </span>
        )}
        {collapsed ? <ChevronDown size={13} className="text-slate-400" /> : <ChevronUp size={13} className="text-slate-400" />}
      </button>

      {!collapsed && (
        <div className="divide-y divide-stone-50">

          {/* ── Approve / Deny time off ─────────────────────────────────── */}
          {approvals.length > 0 && (
            <div>
              <p className="px-4 pt-3 pb-1.5 text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                Pending Time Off
              </p>
              {approvals.map((req) => (
                <div key={req.id} className="px-4 py-2.5 flex items-center gap-2.5">
                  <Avatar name={req.staff} size="xs" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[12.5px] font-medium text-slate-800 leading-snug">{req.staff}</p>
                    <p className="text-[11px] text-slate-400">{req.dates} · {req.type}</p>
                    {req.affectsItems > 0 && (
                      <p className="text-[10px] text-amber-600">{req.affectsItems} items affected</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => handleApprove(req.id, req.staff)}
                      className="flex items-center gap-1 px-2 py-1 text-[11px] font-medium bg-emerald-50 text-emerald-700 hover:bg-emerald-100 rounded-md transition-colors"
                      aria-label={`Approve ${req.staff}`}
                    >
                      <CheckCircle size={11} /> Approve
                    </button>
                    <button
                      onClick={() => handleDeny(req.id, req.staff)}
                      className="flex items-center gap-1 px-2 py-1 text-[11px] font-medium bg-red-50 text-red-600 hover:bg-red-100 rounded-md transition-colors"
                      aria-label={`Deny ${req.staff}`}
                    >
                      <XCircle size={11} /> Deny
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ── Resolve urgent gaps ─────────────────────────────────────── */}
          {urgentGaps.length > 0 && (
            <div>
              <p className="px-4 pt-3 pb-1.5 text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                Urgent Gaps
              </p>
              {urgentGaps.map((gap) => (
                <div key={gap.id} className="px-4 py-2.5 flex items-start gap-2.5">
                  <div className="flex-1 min-w-0">
                    <p className="text-[12.5px] font-medium text-slate-800 leading-snug">{gap.title}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <BranchTag branch={gap.branch as Branch} size="xs" />
                      <span className="text-[10px] text-red-600 font-medium">{gap.due_label ?? 'Urgent'}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => handleResolveGap(gap.id, gap.title)}
                    className="flex items-center gap-1 px-2 py-1 text-[11px] font-medium bg-teal-50 text-teal-700 hover:bg-teal-100 rounded-md transition-colors shrink-0"
                    aria-label={`Resolve: ${gap.title}`}
                  >
                    <CheckSquare size={11} /> Resolve
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* ── Quick reply to room ─────────────────────────────────────── */}
          <div>
            <p className="px-4 pt-3 pb-1.5 text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
              Reply to Room
            </p>
            <div className="px-4 pb-3">
              <div className="flex items-center gap-1.5 mb-2">
                <MessageSquare size={11} className="text-indigo-400" />
                <span className="text-[11px] font-medium text-indigo-600">#{QUICK_REPLY_ROOM.name}</span>
              </div>
              <div className="flex items-center gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSendReply(); }}
                  placeholder="Type a message…"
                  className="flex-1 text-[12.5px] bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-indigo-300 focus:border-indigo-300 transition-colors"
                />
                <button
                  onClick={handleSendReply}
                  disabled={!replyText.trim() || sending}
                  className="p-2 rounded-lg bg-indigo-500 hover:bg-indigo-600 text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  aria-label="Send message"
                >
                  <Send size={13} />
                </button>
              </div>
            </div>
          </div>

          {/* Empty state */}
          {approvals.length === 0 && urgentGaps.length === 0 && (
            <div className="px-4 py-4 text-center">
              <p className="text-[12px] text-slate-400">No pending actions right now.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
